import { useCallback, useMemo, useState } from "react";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import type { BulkNurseJobsBlockedMode, TeamNurse } from "../../utils/models";
import { type BulkJobPicks, type BulkJobStepId, INITIAL_PICKS, jobsHeldBy, toBulkJobsRequest } from "./bulkJobChange";
import { BulkJobsRefreshError, useBulkNurseJobs } from "./useBulkNurseJobs";

/**
 * Default for the blocked-employees choice (DEFAULT: an explicit product decision, not a fallback):
 * finishing the job change is what the admin opened the dialog to do.
 */
const DEFAULT_BLOCKED_MODE: BulkNurseJobsBlockedMode = "move_shifts";

/**
 * State and transitions for the bulk "Change job" wizard. The step only advances once the preview
 * resolves, and Back throws that preview away — it described picks the admin is now changing.
 *
 * `selectedNurses` is snapshotted on mount: the page's selection is re-derived from the roster, which
 * refreshes right after an apply, and the names and the "N selected" count must not shift under the
 * dialog mid-flow. `onApplied` receives the ids that could not be updated, so the page can keep them selected.
 */
export const useBulkJobChangeFlow = (selectedNurses: TeamNurse[], onClose: () => void, onApplied: (failedNurseIds: number[]) => void) => {
	const [nurses] = useState(selectedNurses);
	const { chosenDepartment } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const { preview, apply } = useBulkNurseJobs();
	const [stepId, setStepId] = useState<BulkJobStepId>("choose");
	const [picks, setPicks] = useState<BulkJobPicks>(INITIAL_PICKS);
	const [blockedMode, setBlockedMode] = useState<BulkNurseJobsBlockedMode>(DEFAULT_BLOCKED_MODE);

	// Full catalog, not just enabled jobs: names for jobs a nurse holds that the department no longer offers.
	// DEFAULT: same as `useJobs` — a department payload without a catalog has no job names to offer.
	const catalog = useMemo(() => chosenDepartment.jobs ?? [], [chosenDepartment.jobs]);
	const jobById = useMemo(() => new Map(catalog.map((job) => [job.id, job])), [catalog]);
	const heldJobs = useMemo(() => jobsHeldBy(nurses, catalog), [nurses, catalog]);
	const nameById = useMemo(() => new Map(nurses.map((nurse) => [nurse.id, formatPersonName(nurse)])), [nurses, formatPersonName]);
	const nurseIds = useMemo(() => nurses.map((nurse) => nurse.id), [nurses]);
	const request = toBulkJobsRequest(picks, nurseIds);

	const handleContinue = useCallback(() => {
		if (!request) return;
		setBlockedMode(DEFAULT_BLOCKED_MODE);
		preview.mutate(request, { onSuccess: () => setStepId("review") });
	}, [preview, request]);

	const handleBack = useCallback(() => {
		preview.reset();
		setStepId("choose");
	}, [preview]);

	// Applies exactly what the review step showed — the previewed request, not one rebuilt from live state.
	const previewedRequest = preview.variables;
	const handleApply = useCallback(() => {
		if (!previewedRequest) return;
		apply.mutate(
			{ ...previewedRequest, blockedMode },
			{
				onSuccess: (result) => onApplied(result.failed),
				// The write landed even though the refresh didn't — closing is the honest state, not "try again".
				onError: (error) => {
					if (error instanceof BulkJobsRefreshError) onApplied(error.result.failed);
				},
			},
		);
	}, [apply, previewedRequest, blockedMode, onApplied]);

	// A batch on the wire can't be walked away from: close, backdrop and Esc all wait for it.
	const handleClose = useCallback(() => {
		if (apply.isPending) return;
		onClose();
	}, [apply.isPending, onClose]);

	return {
		nurses,
		stepId,
		setStepId,
		picks,
		setPicks,
		blockedMode,
		setBlockedMode,
		heldJobs,
		nameById,
		request,
		toJob: request ? jobById.get(request.toJobId) : undefined,
		fromJob: request?.fromJobId ? jobById.get(request.fromJobId) : undefined,
		preview,
		isApplying: apply.isPending,
		handleContinue,
		handleBack,
		handleApply,
		handleClose,
	};
};
